import React from 'react';
import { Link } from 'react-router-dom';
import clsx from 'clsx';
import { FaWhatsapp } from 'react-icons/fa';
import { initials as toInitials } from './format';
import { tierGradient, utTier, type UtTier } from './tiers';
import { TokenChips } from './TokenChips';

/*
 * Ultimate Team building blocks. Tokens come from src/styles/ut-theme.css via
 * the ut-* Tailwind namespace; type is Barlow Condensed (font-ut-display) for
 * headings, numbers and buttons, Barlow (font-ut-body) for text.
 */

export const UT_LOGO = '/assets/wnf-logo-removed-bg.png';

// ─── Type ────────────────────────────────────────────────────────────────────

/** Gold, 12px, letter-spaced: the line above a screen title. */
export const Eyebrow: React.FC<{ children: React.ReactNode; className?: string; color?: string }> = ({ children, className, color }) => (
  <span
    className={clsx('font-ut-display text-xs font-bold uppercase tracking-[0.16em]', !color && 'text-ut-acc', className)}
    style={color ? { color } : undefined}
  >
    {children}
  </span>
);

/** 34px/800 uppercase screen title, optionally with an eyebrow and a right-hand slot. */
export const ScreenTitle: React.FC<{
  eyebrow?: React.ReactNode;
  title: React.ReactNode;
  sub?: React.ReactNode;
  right?: React.ReactNode;
  className?: string;
}> = ({ eyebrow, title, sub, right, className }) => (
  <div className={clsx('flex flex-wrap items-end justify-between gap-3', className)}>
    <div className="flex min-w-0 flex-col gap-0.5">
      {eyebrow && <Eyebrow>{eyebrow}</Eyebrow>}
      <h1 className="font-ut-display text-[34px] font-extrabold uppercase leading-none">{title}</h1>
      {sub && <span className="mt-1 text-[13px] text-ut-mut">{sub}</span>}
    </div>
    {right}
  </div>
);

/** 20–22px/800 uppercase section heading with an optional note on the right. */
export const SectionTitle: React.FC<{ children: React.ReactNode; note?: React.ReactNode; className?: string }> = ({
  children,
  note,
  className,
}) => (
  <div className={clsx('flex items-baseline justify-between gap-3', className)}>
    <h2 className="font-ut-display text-[22px] font-extrabold uppercase leading-tight">{children}</h2>
    {note && <span className="text-[13px] text-ut-mut">{note}</span>}
  </div>
);

/** 11px/600 uppercase muted label. */
export const Label: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className }) => (
  <span className={clsx('text-[11px] font-semibold uppercase tracking-[0.14em] text-ut-mut', className)}>{children}</span>
);

// ─── Surfaces ────────────────────────────────────────────────────────────────

type PanelTone = 'card' | 'raised' | 'accent' | 'danger' | 'plain';

const PANEL_TONES: Record<PanelTone, string> = {
  card: 'bg-ut-card border border-ut-line',
  raised: 'bg-gradient-to-br from-ut-card2 to-ut-card border border-ut-line',
  accent: 'bg-ut-acc-soft border border-ut-acc',
  danger: 'bg-ut-bad/10 border border-ut-bad/60',
  plain: 'bg-ut-card',
};

export const Panel: React.FC<
  React.HTMLAttributes<HTMLDivElement> & { tone?: PanelTone; pad?: 'sm' | 'md' | 'lg' | 'none'; radius?: 'md' | 'lg' }
> = ({ tone = 'card', pad = 'md', radius = 'lg', className, children, ...rest }) => (
  <div
    className={clsx(
      PANEL_TONES[tone],
      radius === 'lg' ? 'rounded-[14px]' : 'rounded-xl',
      pad === 'sm' && 'p-3',
      pad === 'md' && 'p-3.5 sm:p-4',
      pad === 'lg' && 'p-5',
      className,
    )}
    {...rest}
  >
    {children}
  </div>
);

/** Stat tile: big condensed number over a small muted label. */
export const StatTile: React.FC<{ value: React.ReactNode; label: React.ReactNode; small?: boolean; valueClassName?: string; className?: string }> = ({
  value,
  label,
  small,
  valueClassName,
  className,
}) => (
  <div className={clsx('rounded-[10px] bg-ut-card px-1.5 py-2.5 text-center', className)}>
    <div className={clsx('font-ut-display font-extrabold', small ? 'text-xl leading-[1.2]' : 'text-2xl leading-tight', valueClassName)}>{value}</div>
    <div className="text-[11px] tracking-[0.1em] text-ut-mut">{label}</div>
  </div>
);

// ─── Controls ────────────────────────────────────────────────────────────────

type ButtonVariant = 'primary' | 'secondary' | 'danger' | 'ghost' | 'pack';

interface UtButtonBase {
  variant?: ButtonVariant;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  block?: boolean;
  className?: string;
  children: React.ReactNode;
}

const BUTTON_VARIANTS: Record<ButtonVariant, string> = {
  primary: 'bg-ut-acc text-ut-acc-ink font-ut-display font-extrabold tracking-[0.06em] uppercase hover:brightness-110',
  secondary: 'border border-ut-line text-ut-ink font-semibold hover:bg-ut-card2',
  danger: 'border border-ut-bad text-ut-bad font-semibold hover:bg-ut-bad/10',
  ghost: 'text-ut-mut font-semibold hover:text-ut-ink',
  pack: 'bg-gradient-to-br from-[#f7de8e] to-[#b0842a] text-[#1a1204] font-ut-display font-extrabold tracking-[0.06em] uppercase hover:brightness-105',
};

const BUTTON_SIZES = {
  sm: 'px-3 py-2 text-[13px] rounded-lg',
  md: 'px-4 py-2.5 text-sm rounded-[10px]',
  lg: 'px-4 py-3.5 text-[18px] rounded-xl',
  xl: 'px-4 py-[18px] text-2xl rounded-xl',
};

/** Primary (gold), secondary (outlined), danger, ghost or pack (gold gradient) button. Renders a Link when `to` is set. */
export const UtButton: React.FC<
  UtButtonBase & ({ to: string; onClick?: never; type?: never; disabled?: never } | ({ to?: undefined } & React.ButtonHTMLAttributes<HTMLButtonElement>))
> = (props) => {
  const { variant = 'primary', size = 'md', block, className, children } = props;
  const cls = clsx(
    'inline-flex items-center justify-center gap-2 text-center transition disabled:cursor-not-allowed disabled:opacity-50',
    BUTTON_VARIANTS[variant],
    variant === 'primary' || variant === 'pack'
      ? BUTTON_SIZES[size].replace('text-sm', 'text-base').replace('text-[13px]', 'text-sm')
      : BUTTON_SIZES[size],
    block && 'w-full',
    className,
  );
  if (props.to !== undefined) {
    return (
      <Link to={props.to} className={cls}>
        {children}
      </Link>
    );
  }
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { variant: _v, size: _s, block: _b, className: _c, children: _ch, to: _t, type = 'button', ...rest } = props;
  return (
    <button type={type} className={cls} {...rest}>
      {children}
    </button>
  );
};

/** 44×26 toggle. On = gold track with an accInk knob on the right. */
export const UtToggle: React.FC<{ checked: boolean; onChange?: (next: boolean) => void; disabled?: boolean; label?: string }> = ({
  checked,
  onChange,
  disabled,
  label,
}) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    aria-label={label}
    disabled={disabled}
    onClick={() => onChange?.(!checked)}
    className={clsx(
      'relative h-[26px] w-11 flex-none rounded-[13px] border border-ut-line transition-colors disabled:opacity-50',
      checked ? 'bg-ut-acc' : 'bg-ut-card2',
    )}
  >
    <span
      className={clsx(
        'absolute top-[2px] h-5 w-5 rounded-full transition-all',
        checked ? 'left-[20px] bg-ut-acc-ink' : 'left-[2px] bg-ut-mut',
      )}
    />
  </button>
);

export interface SegmentOption<T extends string> {
  value: T;
  label: React.ReactNode;
}

/** Bordered segmented control; the active segment is gold with accInk text. */
export function Segmented<T extends string>({
  options,
  value,
  onChange,
  className,
}: {
  options: SegmentOption<NoInfer<T>>[];
  value: T;
  onChange: (v: NoInfer<T>) => void;
  className?: string;
}) {
  return (
    <div role="tablist" className={clsx('flex overflow-hidden rounded-[10px] border border-ut-line', className)}>
      {options.map((o) => {
        const active = o.value === value;
        return (
          <button
            key={o.value}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onChange(o.value)}
            className={clsx(
              'whitespace-nowrap px-4 py-2 font-ut-display text-[15px] font-bold uppercase tracking-[0.06em] transition-colors',
              active ? 'bg-ut-acc text-ut-acc-ink' : 'text-ut-mut hover:text-ut-ink',
            )}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

/** Pill-shaped filter chip. */
export const Chip: React.FC<{ active?: boolean; onClick?: () => void; children: React.ReactNode; className?: string }> = ({
  active,
  onClick,
  children,
  className,
}) => (
  <button
    type="button"
    onClick={onClick}
    aria-pressed={active}
    className={clsx(
      'flex-none whitespace-nowrap rounded-full border px-3 py-1.5 font-ut-display text-sm font-bold tracking-[0.06em] transition-colors',
      active ? 'border-ut-acc bg-ut-acc-soft text-ut-acc' : 'border-ut-line text-ut-mut hover:text-ut-ink',
      className,
    )}
  >
    {children}
  </button>
);

type PillTone = 'gold' | 'mut' | 'ok' | 'bad' | 'blue' | 'orange' | 'sky';

const PILL_TONES: Record<PillTone, string> = {
  gold: 'bg-ut-acc-soft text-ut-acc',
  mut: 'bg-ut-card2 text-ut-mut',
  ok: 'bg-ut-ok/15 text-ut-ok',
  bad: 'bg-ut-bad/15 text-ut-bad',
  blue: 'bg-ut-blue/15 text-ut-blue',
  orange: 'bg-ut-orange/15 text-ut-orange',
  sky: 'bg-ut-sky/15 text-ut-sky',
};

/** Small status pill (phases, PAID/UNPAID, token states). */
export const StatusPill: React.FC<{ tone?: PillTone; children: React.ReactNode; className?: string }> = ({ tone = 'mut', children, className }) => (
  <span
    className={clsx(
      'inline-flex items-center whitespace-nowrap rounded-md px-2 py-1 font-ut-display text-xs font-bold uppercase tracking-[0.08em]',
      PILL_TONES[tone],
      className,
    )}
  >
    {children}
  </span>
);

// ─── Form fields ─────────────────────────────────────────────────────────────

const fieldCls =
  'w-full rounded-[10px] border border-ut-line bg-ut-field px-3.5 py-3 font-ut-body text-[15px] font-medium text-ut-ink outline-none transition focus:border-ut-acc disabled:opacity-60';

/** Labelled input. The label is 12px/600 uppercase muted, as in the design. */
export const UtField = React.forwardRef<
  HTMLInputElement,
  React.InputHTMLAttributes<HTMLInputElement> & { label?: React.ReactNode; hint?: React.ReactNode; error?: React.ReactNode }
>(({ label, hint, error, className, ...rest }, ref) => (
  <label className="flex flex-col gap-1.5 text-xs font-semibold uppercase tracking-[0.1em] text-ut-mut">
    {label}
    <input ref={ref} className={clsx(fieldCls, error && 'border-ut-bad', className)} {...rest} />
    {hint && <span className="text-[11px] font-medium normal-case tracking-normal">{hint}</span>}
    {error && <span className="text-[12px] font-medium normal-case tracking-normal text-ut-bad">{error}</span>}
  </label>
));
UtField.displayName = 'UtField';

export const UtSelect: React.FC<React.SelectHTMLAttributes<HTMLSelectElement> & { label?: React.ReactNode; hint?: React.ReactNode }> = ({
  label,
  hint,
  className,
  children,
  ...rest
}) => (
  <label className="flex flex-col gap-1.5 text-xs font-semibold uppercase tracking-[0.1em] text-ut-mut">
    {label}
    <select className={clsx(fieldCls, 'appearance-auto', className)} {...rest}>
      {children}
    </select>
    {hint && <span className="text-[11px] font-medium normal-case tracking-normal">{hint}</span>}
  </label>
);

export const UtTextarea: React.FC<React.TextareaHTMLAttributes<HTMLTextAreaElement> & { label?: React.ReactNode }> = ({ label, className, ...rest }) => (
  <label className="flex flex-col gap-1.5 text-xs font-semibold uppercase tracking-[0.1em] text-ut-mut">
    {label}
    <textarea className={clsx(fieldCls, 'min-h-[96px]', className)} {...rest} />
  </label>
);

/** Search box with the field look. */
export const UtSearch: React.FC<React.InputHTMLAttributes<HTMLInputElement>> = ({ className, ...rest }) => (
  <input type="search" className={clsx(fieldCls, className)} {...rest} />
);

// ─── Player bits ─────────────────────────────────────────────────────────────

/** Initials on the tier gradient; the square avatar used in rows. */
export const BadgeSquare: React.FC<{
  name: string;
  /** Overlays the player's token badges (priority, shield, injury) on the top-right corner. */
  playerId?: string | null;
  rarity?: string | null;
  xp?: number | null;
  tier?: UtTier;
  size?: number;
  className?: string;
}> = ({ name, playerId, rarity, xp, tier, size = 34, className }) => {
  const t = tier ?? utTier(rarity, xp);
  if (playerId) {
    return (
      <span className={clsx('relative flex flex-none', className)}>
        <BadgeSquare name={name} rarity={rarity} xp={xp} tier={t} size={size} />
        <TokenChips playerId={playerId} variant="inline" size={Math.max(12, Math.round(size * 0.42))} className="absolute -right-1.5 -top-1.5 gap-0.5" />
      </span>
    );
  }
  return (
    <span
      aria-hidden
      className={clsx('flex flex-none items-center justify-center font-ut-display font-extrabold', className)}
      style={{
        width: size,
        height: size,
        borderRadius: size >= 36 ? 8 : 6,
        background: tierGradient(t),
        color: t.ink,
        fontSize: Math.max(11, Math.round(size * 0.37)),
      }}
    >
      {toInitials(name)}
    </span>
  );
};

export type FormResult = 'W' | 'D' | 'L' | '—';

const FORM_BG: Record<FormResult, string> = { W: '#37d27f', D: '#a3a9b6', L: '#ff6060', '—': 'rgb(var(--ut-card2))' };

/** Last-N form chips: 36×36, W/D/L colour, dark letter. */
export const FormChips: React.FC<{ results: FormResult[]; size?: number }> = ({ results, size = 36 }) => (
  <div className="flex gap-1.5">
    {results.map((r, i) => (
      <span
        key={i}
        className="flex items-center justify-center rounded-lg font-ut-display font-extrabold"
        style={{ width: size, height: size, background: FORM_BG[r], color: r === '—' ? 'rgb(var(--ut-mut))' : '#0b0d12', fontSize: size / 2 }}
      >
        {r}
      </span>
    ))}
  </div>
);

/** A clickable list row: badge, name and sub line, and a right-hand value. */
export const PlayerRow: React.FC<{
  name: string;
  /** Shows their token badges (priority, shield, injury) after the name. */
  playerId?: string | null;
  /** Small inline mark after the name (e.g. WhatsAppMark). */
  nameAdornment?: React.ReactNode;
  sub?: React.ReactNode;
  rarity?: string | null;
  xp?: number | null;
  right?: React.ReactNode;
  lead?: React.ReactNode;
  to?: string;
  onClick?: () => void;
  badgeSize?: number;
  className?: string;
}> = ({ name, playerId, nameAdornment, sub, rarity, xp, right, lead, to, onClick, badgeSize = 34, className }) => {
  const inner = (
    <>
      {lead}
      <BadgeSquare name={name} rarity={rarity} xp={xp} size={badgeSize} />
      <span className="flex min-w-0 flex-1 flex-col">
        <span className="flex min-w-0 items-center gap-1.5">
          <span className="truncate text-[15px] font-semibold">{name}</span>
          {nameAdornment}
          {playerId && <TokenChips playerId={playerId} variant="inline" />}
        </span>
        {sub && <span className="truncate text-xs text-ut-mut">{sub}</span>}
      </span>
      {right}
    </>
  );
  const cls = clsx('flex w-full items-center gap-2.5 px-3.5 py-2.5 text-left', (to || onClick) && 'transition-colors hover:bg-ut-card2', className);
  if (to) {
    return (
      <Link to={to} className={cls}>
        {inner}
      </Link>
    );
  }
  if (onClick) {
    return (
      <button type="button" onClick={onClick} className={cls}>
        {inner}
      </button>
    );
  }
  return <div className={cls}>{inner}</div>;
};

// ─── States ──────────────────────────────────────────────────────────────────

export const UtLoading: React.FC<{ label?: string; className?: string }> = ({ label = 'Loading', className }) => (
  <div className={clsx('flex flex-col items-center justify-center gap-3 py-16 text-ut-mut', className)} role="status">
    <span className="h-8 w-8 animate-spin rounded-full border-2 border-ut-line border-t-ut-acc" />
    <span className="font-ut-display text-sm font-bold uppercase tracking-[0.16em]">{label}</span>
  </div>
);

export const UtEmpty: React.FC<{ title: React.ReactNode; body?: React.ReactNode; action?: React.ReactNode; className?: string }> = ({
  title,
  body,
  action,
  className,
}) => (
  <Panel className={clsx('flex flex-col items-center gap-2 py-10 text-center', className)}>
    <span className="font-ut-display text-xl font-extrabold uppercase">{title}</span>
    {body && <span className="max-w-md text-sm text-ut-mut">{body}</span>}
    {action && <div className="mt-2">{action}</div>}
  </Panel>
);

export const UtError: React.FC<{ message: React.ReactNode; onRetry?: () => void }> = ({ message, onRetry }) => (
  <Panel tone="danger" className="flex flex-wrap items-center justify-between gap-3">
    <span className="text-sm font-semibold text-ut-bad">{message}</span>
    {onRetry && (
      <UtButton variant="danger" size="sm" onClick={onRetry}>
        Try again
      </UtButton>
    )}
  </Panel>
);

/** Full-screen centred modal sheet in UT styling. */
export const UtModal: React.FC<{ open: boolean; onClose: () => void; title?: React.ReactNode; children: React.ReactNode; wide?: boolean }> = ({
  open,
  onClose,
  title,
  children,
  wide,
}) => {
  React.useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);
  if (!open) return null;
  return (
    <div className="ut-root fixed inset-0 z-[60] flex items-end justify-center bg-black/60 p-0 sm:items-center sm:p-4" onClick={onClose}>
      <div
        role="dialog"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
        className={clsx(
          'max-h-[92vh] w-full overflow-y-auto rounded-t-[18px] border border-ut-line bg-ut-bg2 p-4 font-ut-body text-ut-ink sm:rounded-[18px] sm:p-5',
          wide ? 'sm:max-w-3xl' : 'sm:max-w-lg',
        )}
      >
        {title && (
          <div className="mb-4 flex items-center justify-between gap-3">
            <h2 className="font-ut-display text-2xl font-extrabold uppercase leading-none">{title}</h2>
            <button type="button" onClick={onClose} className="rounded-lg border border-ut-line px-2.5 py-1.5 font-ut-display text-xs font-bold tracking-[0.12em] text-ut-mut">
              CLOSE
            </button>
          </div>
        )}
        {children}
      </div>
    </div>
  );
};

/** Subtle mark for WNF WhatsApp group members (translucent, inherits the muted text colour). */
export const WhatsAppMark: React.FC<{ size?: number; className?: string }> = ({ size = 13, className }) => (
  <span title="WhatsApp group member" aria-label="WhatsApp group member" role="img" className={clsx('inline-flex flex-none text-ut-mut opacity-60', className)}>
    <FaWhatsapp size={size} aria-hidden />
  </span>
);
